import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChartDataPoint, TimeSeriesData } from '../../types/analytics';
import { AnalyticsCalculations } from '../../utils/analyticsCalculations.ts';

interface BaseChartProps {
  data: any[];
  height?: number;
  className?: string;
}

interface LineChartProps extends BaseChartProps {
  xKey: string;
  yKey: string;
  lineColor?: string;
  showGrid?: boolean;
  showLegend?: boolean;
}

interface AreaChartProps extends BaseChartProps {
  xKey: string;
  yKey: string;
  areaColor?: string;
  showGrid?: boolean;
  showLegend?: boolean;
}

interface BarChartProps extends BaseChartProps {
  xKey: string;
  yKey: string;
  barColor?: string;
  showGrid?: boolean;
  showLegend?: boolean;
}

interface PieChartProps extends BaseChartProps {
  dataKey: string;
  nameKey: string;
  colors?: string[];
  showLegend?: boolean;
}

// Color palettes
const CHART_COLORS = {
  primary: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
  blue: ['#1E40AF', '#3B82F6', '#60A5FA', '#93C5FD', '#DBEAFE'],
  green: ['#065F46', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0'],
  purple: ['#581C87', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE'],
  gradient: ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe']
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, formatter }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {`${entry.name}: ${formatter ? formatter(entry.value) : entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Line Chart Component
export const CustomLineChart: React.FC<LineChartProps> = ({
  data,
  xKey,
  yKey,
  lineColor = CHART_COLORS.primary[0],
  height = 300,
  showGrid = true,
  showLegend = false,
  className = ''
}) => {
  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
          <XAxis 
            dataKey={xKey} 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => AnalyticsCalculations.formatCurrency(value)}
          />
          <Tooltip 
            content={<CustomTooltip formatter={AnalyticsCalculations.formatCurrency} />}
          />
          {showLegend && <Legend />}
          <Line 
            type="monotone" 
            dataKey={yKey} 
            stroke={lineColor}
            strokeWidth={2}
            dot={{ fill: lineColor, strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: lineColor, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Area Chart Component
export const CustomAreaChart: React.FC<AreaChartProps> = ({
  data,
  xKey,
  yKey,
  areaColor = CHART_COLORS.primary[0],
  height = 300,
  showGrid = true,
  showLegend = false,
  className = ''
}) => {
  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
          <XAxis 
            dataKey={xKey} 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => AnalyticsCalculations.formatCurrency(value)}
          />
          <Tooltip 
            content={<CustomTooltip formatter={AnalyticsCalculations.formatCurrency} />}
          />
          {showLegend && <Legend />}
          <Area 
            type="monotone" 
            dataKey={yKey} 
            stroke={areaColor}
            strokeWidth={2}
            fill={areaColor}
            fillOpacity={0.1}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Bar Chart Component
export const CustomBarChart: React.FC<BarChartProps> = ({
  data,
  xKey,
  yKey,
  barColor = CHART_COLORS.primary[0],
  height = 300,
  showGrid = true,
  showLegend = false,
  className = ''
}) => {
  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
          <XAxis 
            dataKey={xKey} 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => AnalyticsCalculations.formatCurrency(value)}
          />
          <Tooltip 
            content={<CustomTooltip formatter={AnalyticsCalculations.formatCurrency} />}
          />
          {showLegend && <Legend />}
          <Bar 
            dataKey={yKey} 
            fill={barColor}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Pie Chart Component
export const CustomPieChart: React.FC<PieChartProps> = ({
  data,
  dataKey,
  nameKey,
  colors = CHART_COLORS.primary,
  height = 300,
  showLegend = true,
  className = ''
}) => {
  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            outerRadius={80}
            fill="#8884d8"
            dataKey={dataKey}
            nameKey={nameKey}
            label={({ name, percent }) => `${name} ${percent ? (percent * 100).toFixed(0) : 0}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value: any) => [AnalyticsCalculations.formatCurrency(value), 'Amount']}
          />
          {showLegend && <Legend />}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// Multi-line Chart Component
interface MultiLineChartProps extends BaseChartProps {
  xKey: string;
  lines: Array<{
    key: string;
    name: string;
    color: string;
  }>;
  showGrid?: boolean;
  showLegend?: boolean;
}

export const MultiLineChart: React.FC<MultiLineChartProps> = ({
  data,
  xKey,
  lines,
  height = 300,
  showGrid = true,
  showLegend = true,
  className = ''
}) => {
  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
          <XAxis 
            dataKey={xKey} 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => AnalyticsCalculations.formatCurrency(value)}
          />
          <Tooltip 
            content={<CustomTooltip formatter={AnalyticsCalculations.formatCurrency} />}
          />
          {showLegend && <Legend />}
          {lines.map((line, index) => (
            <Line
              key={line.key}
              type="monotone"
              dataKey={line.key}
              name={line.name}
              stroke={line.color}
              strokeWidth={2}
              dot={{ fill: line.color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: line.color, strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// KPI Card Component
interface KPICardProps {
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'increase' | 'decrease' | 'neutral';
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'indigo';
  className?: string;
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  change,
  changeType = 'neutral',
  icon,
  color = 'blue',
  className = ''
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    purple: 'bg-purple-50 border-purple-200 text-purple-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900'
  };

  const changeColorClasses = {
    increase: 'text-green-600',
    decrease: 'text-red-600',
    neutral: 'text-gray-600'
  };

  const changeIcon = {
    increase: '↗',
    decrease: '↘',
    neutral: '→'
  };

  return (
    <div className={`p-6 rounded-lg border-2 ${colorClasses[color]} ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium opacity-75">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {change !== undefined && (
            <p className={`text-sm mt-1 ${changeColorClasses[changeType]}`}>
              {changeIcon[changeType]} {Math.abs(change)}%
            </p>
          )}
        </div>
        {icon && (
          <div className="ml-4 opacity-75">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

// Chart Container Component
interface ChartContainerProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
}

export const ChartContainer: React.FC<ChartContainerProps> = ({
  title,
  children,
  className = '',
  actions
}) => {
  return (
    <div className={`bg-white rounded-lg shadow-md border border-gray-200 ${className}`}>
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {actions && <div className="flex items-center space-x-2">{actions}</div>}
        </div>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
};

// Loading Chart Component
export const ChartLoading: React.FC<{ height?: number }> = ({ height = 300 }) => {
  return (
    <div className="w-full animate-pulse" style={{ height }}>
      <div className="bg-gray-200 rounded h-full flex items-center justify-center">
        <div className="text-gray-500">Loading chart...</div>
      </div>
    </div>
  );
};

// No Data Chart Component
export const ChartNoData: React.FC<{ height?: number; message?: string }> = ({ 
  height = 300, 
  message = "No data available" 
}) => {
  return (
    <div className="w-full bg-gray-50 rounded border-2 border-dashed border-gray-300 flex items-center justify-center" style={{ height }}>
      <div className="text-center">
        <div className="text-gray-400 text-4xl mb-2">📊</div>
        <p className="text-gray-500">{message}</p>
      </div>
    </div>
  );
};
